import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Crown, Sparkles, CheckCircle, Users, Calendar, Award, Gift, TrendingUp, Shield } from 'lucide-react';
export function DirectAccessSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-5 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-149" data-mp="2ysuafalimvym">
      {/* Mystical background effects - matching reference snippet */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-150" data-mp="37szdx9nl9a7i" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-151" data-mp="3qlbi81xsylka" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-152" data-mp="13z3kmmwba99s" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-153" data-mp="18zhw6ex4g1ck">
        {/* MAIN SECTION: ONGOING DIRECT ACCESS */}
        <div className="relative mb-12 sm:mb-20" data-id="element-154" data-mp="1ivkybkrz3zzi">
          <div className="absolute inset-0 bg-gradient-radial from-purple-600/20 via-transparent to-transparent blur-3xl" data-id="element-155" data-mp="1ixfigwvf7o2j" />

          <div className="relative bg-gradient-to-br from-purple-950/60 via-stone-950/80 to-black border-2 sm:border-4 border-purple-600/60 p-5 sm:p-10 md:p-12 rounded-lg shadow-[0_0_16px_rgba(168,85,247,0.3)] sm:shadow-[0_0_60px_rgba(168,85,247,0.5)]" data-id="element-156" data-mp="1mvln9zcp7tkt">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12" data-id="element-157" data-mp="21of2djo267oy">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-transparent" data-id="element-158" data-mp="26mgjw05u5yyb" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-purple-500 to-transparent" data-id="element-159" data-mp="2ank0iovpjz0l" />
            </div>
            <div className="absolute top-0 right-0 w-12 h-12" data-id="element-160" data-mp="bfwckoq7vtfw">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-purple-500 to-transparent" data-id="element-161" data-mp="6kgz3xw4fzk0" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-purple-500 to-transparent" data-id="element-162" data-mp="kbvkucheyeur" />
            </div>
            <div className="absolute bottom-0 left-0 w-12 h-12" data-id="element-163" data-mp="u8rvoousbd2h">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-transparent" data-id="element-164" data-mp="u6xb5bnfdmyx" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-purple-500 to-transparent" data-id="element-165" data-mp="pc1h65ymopj6" />
            </div>
            <div className="absolute bottom-0 right-0 w-12 h-12" data-id="element-166" data-mp="z7bmb2kbpd16">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-purple-500 to-transparent" data-id="element-167" data-mp="z60i51myu39j" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-purple-500 to-transparent" data-id="element-168" data-mp="30zhkbpsbubww" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-8" data-id="element-169" data-mp="2qw2t63osdfta">
              <div className="relative" data-id="element-170" data-mp="2ux4jfv0q6hj4">
                <div className="absolute inset-0 bg-purple-500/40 blur-xl animate-pulse-slow" data-id="element-171" data-mp="2uw377n7z3ozt" />
                <CheckCircle className="relative w-12 h-12 sm:w-16 sm:h-16 text-purple-400 drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]" data-id="element-172" data-mp="2h57we5dwyxtl" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8" data-id="element-173" data-mp="275z6poq305d1">
              <h3 className="text-xl sm:text-3xl md:text-5xl font-serif font-bold text-purple-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
            }} data-id="element-174" data-mp="2c6wm5tjpilau">
                ✓ ONGOING DIRECT ACCESS TO BRENDEN HENRY & DR. ALI MAZLOUM
              </h3>

              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100 mb-4" data-id="element-175" data-mp="3sly82413whul">
                3+ Private Sessions Every Month
              </p>

              <p className="text-xl text-stone-400" data-id="element-176" data-mp="1xh8adarb6i0y">
                (Worth $18,000+ based on our $1,500/hour private consulting
                rate)
              </p>
            </div>

            {/* Opening copy */}
            <div className="space-y-6 text-lg text-stone-300 mb-12" data-id="element-177" data-mp="1nij3ed0yahzh">
              <p className="text-2xl font-bold text-center text-amber-400" style={{
              textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
            }} data-id="element-178" data-mp="1jhpcrbw4ip2w">
                This is the crown jewel.
              </p>

              <p className="text-xl text-center text-stone-100 font-semibold" data-id="element-179" data-mp="19rdwqqz60pfn">
                The part that changes everything.
              </p>

              <p className="text-xl text-stone-100" data-id="element-180" data-mp="14t2mif4ykcww">
                While you're a member, you get direct access to Brenden Henry
                and Dr. Ali Mazloum.
              </p>

              <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 space-y-3" data-id="element-181" data-mp="vrlbvcabl3fl">
                <p className="text-stone-100" data-id="element-182" data-mp="qucq71pyqh9l">
                  Not through some diluted group program where you're one of
                  hundreds fighting for attention.
                </p>
                <p className="text-stone-100" data-id="element-183" data-mp="37e59uzlqyvpo">
                  Not through some forum where your questions get answered by
                  moderators who don't actually know.
                </p>
                <p className="text-xl font-bold text-purple-400" data-id="element-184" data-mp="3gev43yvd0e1a">
                  Direct access to Brenden and Ali themselves.
                </p>
              </div>

              <p className="text-xl font-bold text-stone-100" data-id="element-185" data-mp="2nqz8kxhj0s63">
                Here's what these monthly sessions include:
              </p>
            </div>

            {/* LIVE STRATEGIC BRIEFINGS WITH DR. ALI */}
            <div className="mb-12 sm:mb-16" data-id="element-186" data-mp="2xic1lsf29odp">
              <div className="bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-187" data-mp="2xjddu07tcgx0">
                <div className="flex items-start gap-3 sm:gap-4 mb-6" data-id="element-188" data-mp="250hmhbr78utp">
                  <Users className="w-7 h-7 sm:w-10 sm:h-10 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-189" data-mp="38bx0mqe6k96c" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-emerald-400" data-id="element-190" data-mp="33ctvl5y7a2yd">
                    LIVE STRATEGIC BRIEFINGS WITH DR. ALI MAZLOUM
                  </h4>
                </div>

                <p className="text-2xl text-emerald-300 font-bold mb-6" data-id="element-191" data-mp="3d2lv8el5pm9p">
                  The Human MRI Reveals What Your Doctors Miss
                </p>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-192" data-mp="2pdknyuo28h60">
                  <p className="text-stone-100" data-id="element-193" data-mp="2keq03eb8k2f1">
                    Ali breaks down complex health optimization strategies,
                    diagnostic insights, and transformation protocols.
                  </p>

                  <p className="text-stone-100" data-id="element-194" data-mp="2tduxuiez3rq5">
                    This is the same level of intelligence he can provide to our
                    $120,000/year private clients.
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-emerald-500/30 my-6" data-id="element-195" data-mp="8nvqob5r5mol">
                    <p data-id="element-196" data-mp="h4mmrnyxql41">
                      The same analytical frameworks he uses in Russia's largest
                      hospitals.
                    </p>
                    <p data-id="element-197" data-mp="77hy9hhmadk2">
                      The same diagnostic genius that spots what entire medical
                      teams miss.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mb-4" data-id="element-198" data-mp="zvx9dsxmobq4">
                    You'll learn:
                  </p>

                  <div className="space-y-3" data-id="element-199" data-mp="14v9v2acrjcr4">
                    <p className="flex items-start gap-3" data-id="element-200" data-mp="14x34fxergb2t">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-201" data-mp="q52nipoane57">→</span>
                      <span data-id="element-202" data-mp="v4nm2yzjxkhe">
                        How to think diagnostically instead of symptomatically
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-203" data-mp="1isoorra1u6ow">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-204" data-mp="1nmjhfk7mtw04">→</span>
                      <span data-id="element-205" data-mp="3jp641eu0kpqm">
                        How to see patterns in your biomarkers that reveal root
                        causes
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-206" data-mp="39yjrxuliahco">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-207" data-mp="39z30ikp9nte1">→</span>
                      <span data-id="element-208" data-mp="35yj1tcp1ohps">
                        How to identify systemic failures instead of just
                        treating symptoms
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-209" data-mp="1kb6d6heiqu0">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-210" data-mp="3tmk8eg2nxo2k">→</span>
                      <span data-id="element-211" data-mp="3ton8jsxj4xl0">
                        How to design protocols that actually address the source
                        of your issues
                      </span>
                    </p>
                  </div>

                  <div className="bg-emerald-950/30 p-6 rounded-lg mt-6" data-id="element-212" data-mp="2d814ksb05aka">
                    <p className="text-stone-100 mb-2" data-id="element-213" data-mp="2dawz06nn5h3z">This isn't theory.</p>
                    <p className="text-stone-100 mb-2" data-id="element-214" data-mp="sscreuwr1ryp">
                      This is applied clinical intelligence.
                    </p>
                    <p className="text-xl text-emerald-400 font-bold" data-id="element-215" data-mp="11rg8xug9pzb3">
                      The kind of intelligence that saves lives when
                      conventional medicine fails.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* MONTHLY INTELLIGENCE DROPS WITH BRENDEN */}
            <div className="mb-12 sm:mb-16" data-id="element-216" data-mp="1ge9j6laxkewf">
              <div className="bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-217" data-mp="1lens05kcvsvi">
                <div className="flex items-start gap-3 sm:gap-4 mb-6" data-id="element-218" data-mp="1pfhhv4hczhr1">
                  <Sparkles className="w-7 h-7 sm:w-10 sm:h-10 text-cyan-400 flex-shrink-0 mt-0.5" data-id="element-219" data-mp="3n7bxymodwuez" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-cyan-400" data-id="element-220" data-mp="3r7dpw44bi9h2">
                    MONTHLY INTELLIGENCE DROPS WITH BRENDEN HENRY
                  </h4>
                </div>

                <p className="text-2xl text-cyan-300 font-bold mb-6" data-id="element-221" data-mp="1ryeorwyayvin">
                  Inside The Mind That Decoded The Peptide Matrix
                </p>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-222" data-mp="1x0m5t0xzp1ru">
                  <p className="text-stone-100" data-id="element-223" data-mp="21yxg1cs75eal">
                    Brenden shares cutting-edge research, new peptide
                    discoveries, protocol updates, and answers to the questions
                    the community is asking.
                  </p>

                  <p className="text-xl font-bold text-cyan-400" data-id="element-224" data-mp="2fohc5zr9vdax">
                    You get inside his brain each month.
                  </p>

                  <div className="bg-cyan-950/30 border-l-4 border-cyan-500 p-6 my-6" data-id="element-225" data-mp="2kn2blwyp9syb">
                    <p className="text-stone-100 mb-2" data-id="element-226" data-mp="2poi05z036hlg">
                      Not through a polished course recorded years ago.
                    </p>
                    <p className="text-xl font-bold text-cyan-400" data-id="element-227" data-mp="iyekgp96g9gj">
                      But recorded new every month. Current. Responsive to what
                      you actually need to know right now.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mb-4" data-id="element-228" data-mp="90qqi1jeb5zf">
                    He'll break down:
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-cyan-500/30" data-id="element-229" data-mp="11p618bvhv24y">
                    <p data-id="element-230" data-mp="11r0fj7c9fw02">New information as it's released.</p>
                    <p data-id="element-231" data-mp="wre1wwsisdqq">
                      Deeper dives into the most important topics from new
                      angles.
                    </p>
                    <p data-id="element-232" data-mp="n09mjvyxmqvd">Why certain protocols work better than others.</p>
                    <p data-id="element-233" data-mp="my6znkt2tduv">Insights from his latest client transformations.</p>
                    <p data-id="element-234" data-mp="1kgfemw8dyb1t">Misinformation he's seeing spread by influencers.</p>
                  </div>

                  <p className="text-xl text-cyan-400 font-bold mt-6" data-id="element-235" data-mp="1fo5k12wbsmt7">
                    And he'll do it in a way that makes complex pharmacology
                    accessible without dumbing it down.
                  </p>
                </div>
              </div>
            </div>

            {/* LIVE Q&A SESSIONS */}
            <div className="mb-12 sm:mb-16" data-id="element-236" data-mp="304vh7u9ko28n">
              <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-237" data-mp="355j90i5mkn3x">
                <div className="flex items-start gap-3 sm:gap-4 mb-6" data-id="element-238" data-mp="2r4q9tk4dp3uh">
                  <Calendar className="w-7 h-7 sm:w-10 sm:h-10 text-blue-400 flex-shrink-0 mt-0.5" data-id="element-239" data-mp="2v84zl2i4cyl4" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-400" data-id="element-240" data-mp="2h8wwhyiu56bn">
                    LIVE "INNER CIRCLE" Q&A SESSIONS
                  </h4>
                </div>

                <p className="text-2xl text-blue-300 font-bold mb-6" data-id="element-241" data-mp="fg8prf6zg0r4">
                  Get Direct Answers About YOUR Specific Situation
                </p>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-242" data-mp="kdr1tegaqw3c">
                  <p className="text-xl font-bold text-stone-100" data-id="element-243" data-mp="24ynuhc88tbpw">
                    This is where theory meets YOUR reality.
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-blue-500/30" data-id="element-244" data-mp="2enpb94mm3x4x">
                    <p data-id="element-245" data-mp="2epih4hpeidbv">Submit your questions in advance or ask them live.</p>
                    <p data-id="element-246" data-mp="29ve5vm93rj4z">
                      Get direct answers from the masters about YOUR specific
                      situation, YOUR protocols, YOUR results.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mt-6 mb-4" data-id="element-247" data-mp="2irdhnjzicmol">
                    Real questions from members like you:
                  </p>

                  <div className="space-y-6" data-id="element-248" data-mp="2soi4lm16gm6g">
                    <div className="bg-blue-950/30 p-6 rounded-lg" data-id="element-249" data-mp="2slm71yt8rzij">
                      <p className="text-stone-100 mb-3 italic" data-id="element-250" data-mp="1392939g6eyz1">
                        "I'm stacking these three peptides for cognitive
                        enhancement but I'm not seeing the results I expected.
                        What am I missing?"
                      </p>
                      <p className="text-blue-400 font-semibold" data-id="element-251" data-mp="3qfwuau89ucz7">
                        → Brenden can tell you exactly what's wrong and how to
                        fix it.
                      </p>
                    </div>

                    <div className="bg-blue-950/30 p-6 rounded-lg" data-id="element-252" data-mp="3bgzmh01ufsi">
                      <p className="text-stone-100 mb-3 italic" data-id="element-253" data-mp="32sghz11eistw">
                        "My bloodwork shows this pattern but my doctor doesn't
                        know what it means. What's actually happening?"
                      </p>
                      <p className="text-blue-400 font-semibold" data-id="element-254" data-mp="3bq8uxyjnhoe4">
                        → Ali can decode it in minutes.
                      </p>
                    </div>

                    <div className="bg-blue-950/30 p-6 rounded-lg" data-id="element-255" data-mp="2k0e41xg59qnc">
                      <p className="text-stone-100 mb-3 italic" data-id="element-256" data-mp="2o1pm9hwov9li">
                        "I want to address multiple issues — chronic pain,
                        cognitive decline, and hormone optimization. How do I
                        prioritize?"
                      </p>
                      <p className="text-blue-400 font-semibold" data-id="element-257" data-mp="27i1t4tqva3v2">
                        → They will show you exactly how to think about it and
                        sequence your approach in the most effective way.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500 p-8 mt-8" data-id="element-258" data-mp="1stnqaitgafei">
                    <p className="text-xl text-stone-100 mb-4" data-id="element-259" data-mp="1ntsvi46k15o6">
                      This alone would cost you{' '}
                      <span className="text-amber-400 font-bold" data-id="element-260" data-mp="1jtqyi8brl0f8">
                        $1,500 per hour
                      </span>{' '}
                      if you booked Brenden or Ali privately.
                    </p>
                    <p className="text-xl text-stone-100 mb-4" data-id="element-261" data-mp="19vbe0o78spep">
                      You're getting{' '}
                      <span className="text-blue-400 font-bold" data-id="element-262" data-mp="1090eri8cohvs">
                        3+ private sessions every month
                      </span>{' '}
                      for as long as you remain a member.
                    </p>
                    <p className="text-2xl text-amber-400 font-bold" data-id="element-263" data-mp="v7kx805dsti5">
                      That level of ongoing access would otherwise cost thousands
                      at private consulting rates.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mt-6" data-id="element-264" data-mp="1nvqfmg8yq6et">
                    But the real value is incalculable.
                  </p>

                  <p className="text-stone-100" data-id="element-265" data-mp="1e66uf3xs20gs">
                    Because getting the right answer at the right time can:
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-blue-500/30 mt-4" data-id="element-266" data-mp="21uygreooroln">
                    <p className="flex items-start gap-3" data-id="element-267" data-mp="26okkghcvgi5b">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-268" data-mp="3rcvcrfm3ksvm">→</span>
                      <span data-id="element-269" data-mp="3rb27o4r4ugpq">Save you months of trial and error</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-270" data-mp="3mchf8ri4h1bu">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-271" data-mp="4wirc2ilkrgm">→</span>
                      <span data-id="element-272" data-mp="3w3ll91ptlbuj">Prevent costly mistakes</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-273" data-mp="38eapjynbu7n9">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-274" data-mp="38esv5lvvpy7u">→</span>
                      <span data-id="element-275" data-mp="33ghht16dl5kv">Unlock results you didn't know were possible</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-276" data-mp="3hg0gl3dar4ki">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-277" data-mp="3ddvowa6esi0i">→</span>
                      <span data-id="element-278" data-mp="3dg02jju40vdz">Reverse conditions you thought were permanent</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-279" data-mp="wqumsfxjina2">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-280" data-mp="smmuy9vscr7m">→</span>
                      <span data-id="element-281" data-mp="2m6wv4usaplc9">
                        Optimize protocols you thought were already optimized
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* MONTHLY THEMED INTELLIGENCE BRIEFINGS */}
            <div className="mb-12 sm:mb-16" data-id="element-282" data-mp="30v9tfyeuvg9x">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-283" data-mp="30wclwarttqry">
                <div className="flex items-start gap-3 sm:gap-4 mb-6" data-id="element-284" data-mp="39vg3tbf9c04v">
                  <Award className="w-7 h-7 sm:w-10 sm:h-10 text-purple-400 flex-shrink-0 mt-0.5" data-id="element-285" data-mp="39xj2skye13lq" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-400" data-id="element-286" data-mp="3esydx550gleg">
                    ✓ MONTHLY THEMED INTELLIGENCE BRIEFINGS
                  </h4>
                </div>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-287" data-mp="1kj4iccuek2w1">
                  <p className="text-xl font-bold text-stone-100" data-id="element-288" data-mp="1pgnyxjekorr5">
                    Now, each month we will have a theme.
                  </p>

                  <p className="text-stone-100" data-id="element-289" data-mp="1u1kxesqhhkm">
                    Because you're building comprehensive understanding
                    systematically.
                  </p>

                  <p className="text-stone-100" data-id="element-290" data-mp="3tvpzbx1r5quo">
                    Right now we have loosely structured the theme for each
                    month to align with your Peptide Mastery 2.0 content
                    unlocks:
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 my-8" data-id="element-291" data-mp="lj2bt8j681em">
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-292" data-mp="gixw3kq37apg">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-293" data-mp="bjtkknfox0vy">Month 1:</p>
                      <p className="text-sm" data-id="element-294" data-mp="blxmizu13ptl">
                        Foundations & Mass Spec Intelligence
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-295" data-mp="2w90rh6kdck0w">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-296" data-mp="37y8sb85r3uk4">Month 2:</p>
                      <p className="text-sm" data-id="element-297" data-mp="335xfx5ttqgao">
                        Sleep, Circadian & Stress Optimization
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-298" data-mp="2y59tkxgjinms">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-299" data-mp="2t66ml7h027c6">Month 3:</p>
                      <p className="text-sm" data-id="element-300" data-mp="3qvjfvvi0szjq">Gut Health & Detoxification</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-301" data-mp="3lxhw18crsw77">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-302" data-mp="3gwao99amnx0j">Month 4:</p>
                      <p className="text-sm" data-id="element-303" data-mp="3bxr2p9zy4n8p">
                        Brain Regeneration & Cognitive Enhancement
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-304" data-mp="20jlycfvtmbcu">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-305" data-mp="g1l6ce73wyif">Month 5:</p>
                      <p className="text-sm" data-id="element-306" data-mp="fzi6l2g5jr0i">
                        Cardiovascular & Metabolic Optimization
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-307" data-mp="pypq67dhchw2">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-308" data-mp="uriw2ykopw9q">Month 6:</p>
                      <p className="text-sm" data-id="element-309" data-mp="zq3nq9lvf7mk">Joint, Bone & Cartilage Repair</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-310" data-mp="13qe4mhb7big7">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-311" data-mp="18pqmsor4o1cg">Month 7:</p>
                      <p className="text-sm" data-id="element-312" data-mp="1dpbrzgww55xm">Energy, Performance & Endurance</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-313" data-mp="3ahveq08mmyn6">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-314" data-mp="3fgge5xg21eak">Month 8:</p>
                      <p className="text-sm" data-id="element-315" data-mp="3fijepbeu2ptj">Men's Hormonal Optimization</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-316" data-mp="37htvb42ca1yp">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-317" data-mp="33gru4hov3fu3">Month 9:</p>
                      <p className="text-sm" data-id="element-318" data-mp="3hfhrlyflfhiz">Women's Hormonal Balance</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-319" data-mp="3cdsdub0zkpr7">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-320" data-mp="3qdbiuuyi3kza">
                        Month 10:
                      </p>
                      <p className="text-sm" data-id="element-321" data-mp="3mbgj3uw7xfnm">
                        Injury Repair & Vision Restoration
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-322" data-mp="3v6wam59i3iq1">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-323" data-mp="dy9c3l8c8trf">
                        Month 11:
                      </p>
                      <p className="text-sm" data-id="element-324" data-mp="8wa8qbcllwuh">
                        Immune Defense & Respiratory Health
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-325" data-mp="wkqlq47mtl5a">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-326" data-mp="imscfd864ewn">
                        Month 12:
                      </p>
                      <p className="text-sm" data-id="element-327" data-mp="njirbx88l4gh">
                        Anti-Aging, Longevity & Cancer Prevention
                      </p>
                    </div>
                  </div>

                  <p className="text-xl font-bold text-stone-100" data-id="element-328" data-mp="dnpeecqlv8y6">
                    As the monthly themes build on one another, you won't just
                    collect random knowledge.
                  </p>

                  <p className="text-2xl font-bold text-purple-400 text-center my-6" data-id="element-329" data-mp="4uu7lelhp56a">
                    You'll have systematic mastery across every major category
                    of biological optimization.
                  </p>

                  <div className="bg-amber-950/30 border-l-4 border-amber-500 p-6 mt-6" data-id="element-330" data-mp="y7bzxy2fj43m">
                    <p className="text-amber-400 font-bold mb-2" data-id="element-331" data-mp="t88wuj60f7ya">
                      BUT PLEASE NOTE:
                    </p>
                    <p className="text-stone-100" data-id="element-332" data-mp="ogqvhu02r5cs">
                      This is a loose roadmap. The exact theme for each month is
                      subject to change. The actual theme will be specified and
                      confirmed before the next month's sessions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* EXCLUSIVE BONUSES */}
            <div className="mb-12 sm:mb-16" data-id="element-333" data-mp="5jfw89eg0bxc">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-334" data-mp="5khila0wskud">
                <div className="flex items-start gap-3 sm:gap-4 mb-6" data-id="element-335" data-mp="1zwxouatpfyz8">
                  <Gift className="w-7 h-7 sm:w-10 sm:h-10 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-336" data-mp="1uxkznjfd2fth" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-400" data-id="element-337" data-mp="22h3ucmxyvxhw">
                    ✓ EXCLUSIVE ONGOING BONUSES
                  </h4>
                </div>

                <div className="space-y-6 text-lg text-stone-300" data-id="element-338" data-mp="27du7p2ie0ezm">
                  <p className="text-xl text-stone-100 font-semibold" data-id="element-339" data-mp="2c7f8t3fa1p31">
                    As a member of PSI PRO, you'll receive high-value bonuses
                    released throughout your membership.
                  </p>

                  <p className="text-stone-100 font-bold" data-id="element-340" data-mp="1jkm284f7hvl4">Examples include:</p>

                  {/* PURITY REPORTS */}
                  <div className="bg-emerald-950/30 border-l-4 border-emerald-500 p-6 rounded-r-lg" data-id="element-341" data-mp="1tgp213ml5i4w">
                    <h5 className="text-2xl font-bold text-emerald-400 mb-4" data-id="element-342" data-mp="1yf8od553cvxs">
                      PURITY REPORTS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-343" data-mp="34usz4iogzme6">
                      Know Exactly What You're Actually Buying
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-344" data-mp="19rwhtwf37l5q">
                      Whenever we do third-party vendor testing, we'll show you
                      exactly which products are legitimate and which are bunk.
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-345" data-mp="1iskog625ij8l">
                      We send popular peptide products to the lab and share the
                      mass spec results with you.
                    </p>
                    <p className="text-emerald-400 font-bold" data-id="element-346" data-mp="r26doipfpn15">
                      This alone can save you thousands in wasted purchases.
                    </p>
                    <p className="text-stone-400 italic text-sm mt-2" data-id="element-347" data-mp="zv9zbea8h66v">
                      Because buying peptides that don't actually contain what
                      the label claims is worse than not taking anything at all.
                    </p>
                  </div>

                  {/* MEMBERSHIP-ONLY DISCOUNTS */}
                  <div className="bg-blue-950/30 border-l-4 border-blue-500 p-6 rounded-r-lg" data-id="element-348" data-mp="3c3qafwgt58v">
                    <h5 className="text-2xl font-bold text-blue-400 mb-4" data-id="element-349" data-mp="3pci421x3gyxq">
                      MEMBERSHIP-ONLY DISCOUNTS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-350" data-mp="3fg5cb9l40v5m">
                      Potentially Pay For Your Membership Through Savings Alone
                    </p>
                    <p className="text-stone-300 mb-2" data-id="element-351" data-mp="bywwev9y05dj">
                      Get extra discounts that are ONLY available to active
                      members.
                    </p>
                    <div className="space-y-2 pl-4 my-3" data-id="element-352" data-mp="gvnc3hhu4yyf">
                      <p className="text-stone-300" data-id="element-353" data-mp="1zxl14xslvmu">
                        • Exclusive deals on high-quality peptide sources.
                      </p>
                      <p className="text-stone-300" data-id="element-354" data-mp="2mqy39go14n64">
                        • Special pricing on bloodwork and testing.
                      </p>
                      <p className="text-stone-300" data-id="element-355" data-mp="2rqu5pbh2w6jk">
                        • Access to vetted vendors at rates you can't get
                        anywhere else.
                      </p>
                    </div>
                    <p className="text-blue-400 font-bold" data-id="element-356" data-mp="23z3zbw5mjdyb">
                      This can literally pay for months of the membership
                      itself.
                    </p>
                  </div>

                  {/* GUEST ADVISORS */}
                  <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 rounded-r-lg" data-id="element-357" data-mp="2rqj5y14fohfd">
                    <h5 className="text-2xl font-bold text-purple-400 mb-4" data-id="element-358" data-mp="2mqer0fj6bur9">
                      GUEST ADVISORS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-359" data-mp="2hyn88r69etbo">
                      Expand Your Access Beyond Brenden & Ali
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-360" data-mp="3agr4e5x6ujp8">
                      We'll occasionally bring in other trusted doctors,
                      geneticists, or high-quality, vetted professionals who can
                      provide expert guidance in complementary areas.
                    </p>
                    <div className="space-y-2 pl-4" data-id="element-361" data-mp="x5g123mto3ut">
                      <p className="text-stone-300" data-id="element-362" data-mp="x82gsqjv9po4">
                        Longevity physicians who work with billionaires.
                      </p>
                      <p className="text-stone-300" data-id="element-363" data-mp="167yxo600lk8m">
                        Geneticists who understand epigenetic expression.
                      </p>
                      <p className="text-stone-300" data-id="element-364" data-mp="1b464e108izrm">
                        Elite performance coaches who optimize Olympic athletes.
                      </p>
                    </div>
                    <p className="text-purple-400 font-bold mt-3" data-id="element-365" data-mp="1kxws33ojf669">
                      Expanding your intelligence network to a curated circle of
                      true experts.
                    </p>
                  </div>

                  {/* EXCLUSIVE CASE STUDY BREAKDOWNS */}
                  <div className="bg-cyan-950/30 border-l-4 border-cyan-500 p-6 rounded-r-lg" data-id="element-366" data-mp="1yvust69xr43a">
                    <h5 className="text-2xl font-bold text-cyan-400 mb-4" data-id="element-367" data-mp="2z5edjxuode9s">
                      EXCLUSIVE CASE STUDY BREAKDOWNS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-368" data-mp="2q6kpivvetw7u">
                      Learn From Real "Impossible" Transformations
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-369" data-mp="2v6yta1pzak02">
                      See real client transformations with:
                    </p>
                    <div className="space-y-2 pl-4 mb-4" data-id="element-370" data-mp="3mwjxao3qkqpd">
                      <p className="flex items-start gap-3" data-id="element-371" data-mp="3my4s8etyqbnr">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-372" data-mp="394dpbpaqlq10">→</span>
                        <span data-id="element-373" data-mp="3e2yemtogapao">Before/after bloodwork</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-374" data-mp="5nn2jyqxqycm">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-375" data-mp="5m3psi4hn5fl">→</span>
                        <span data-id="element-376" data-mp="aggb6xpiyqtz">Complete protocols used</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-377" data-mp="jsdxqve3ki7y">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-378" data-mp="fsd6ub9taek0">→</span>
                        <span data-id="element-379" data-mp="fqajjz01mziz">Timeline to results</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-380" data-mp="tiqk8t5h2lgq">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-381" data-mp="olgjihcmundl">→</span>
                        <span data-id="element-382" data-mp="ub423altx3t">Obstacles encountered and overcome</span>
                      </p>
                    </div>

                    <p className="text-stone-100 font-bold mb-2" data-id="element-383" data-mp="3szoszrh42esd">
                      Not hypothetical examples. Real transformations.
                    </p>

                    <div className="space-y-4 mt-4 text-sm" data-id="element-384" data-mp="5txi2ey9bhdo">
                      <p className="text-stone-300" data-id="element-385" data-mp="5scpuvzf0aiz">
                        <span className="font-bold text-cyan-400" data-id="element-386" data-mp="1m7nxuiyqnu35">Glen,</span>{' '}
                        the nephrologist who came to us with severe heart
                        failure (29% ejection fraction) and was being evaluated
                        for a transplant. We helped him restore his heart
                        function to normal (52%) and get back to work.
                      </p>
                      <p className="text-stone-300" data-id="element-387" data-mp="1ya733sgrrhe3">
                        <span className="font-bold text-cyan-400" data-id="element-388" data-mp="1y8dtc4av0h1v">Emily,</span>{' '}
                        the lawyer whose cognitive decline was threatening her
                        career. We helped her increase her working memory
                        accuracy from 68% to 93% and cut her processing speed
                        time in half.
                      </p>
                      <p className="text-stone-300" data-id="element-389" data-mp="1fiecu5t34aa2">
                        <span className="font-bold text-cyan-400" data-id="element-390" data-mp="1kb7eulx654ig">
                          Michael,
                        </span>{' '}
                        who could barely walk 160 meters due to osteoarthritis.
                        After our protocol, he improved his walking distance by
                        425% and tapered off his pain meds completely.
                      </p>
                      <p className="text-stone-300" data-id="element-391" data-mp="fc279r3dkcxx">
                        We've even had clients who were told their diabetes was
                        "genetic" and permanent. We helped them lose 120 pounds,
                        get off all medication, and drop their HbA1C from 8.2%
                        to a completely non-diabetic 5.3%.
                      </p>
                    </div>

                    <p className="text-cyan-400 font-bold mt-4" data-id="element-392" data-mp="3sq5i6eblwkzj">
                      Learn from actual case studies that prove what's possible.
                    </p>
                    <p className="text-stone-300 mt-2" data-id="element-393" data-mp="3np9ea0r98yu4">
                      See exactly what was done, why it was done, and what
                      results it produced.
                    </p>
                    <p className="text-stone-300" data-id="element-394" data-mp="3dv92jg1gwvcf">
                      Then apply those insights to your own situation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ONGOING MEMBERSHIP */}
            <div data-id="element-395" data-mp="7autw1oen34o">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-396" data-mp="7beczlpsjxka">
                <div className="flex items-start gap-3 sm:gap-4 mb-6" data-id="element-397" data-mp="3trikr4pcgpn1">
                  <Crown className="w-7 h-7 sm:w-10 sm:h-10 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-398" data-mp="3tpnzfpa5uvif" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-400" data-id="element-399" data-mp="3fu283bggfzmu">
                    ✓ YOUR MEMBERSHIP
                  </h4>
                </div>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-400" data-mp="36svsr5pjkuv6">
                  <p className="text-xl text-stone-100 font-semibold" data-id="element-401" data-mp="3as4lc78uimp5">
                    As you progress month to month, here's what you can count on.
                  </p>

                  <p className="text-xl font-bold text-amber-400 mb-4" data-id="element-402" data-mp="1919m15ffk5do">
                    This means:
                  </p>

                  <div className="space-y-4" data-id="element-403" data-mp="2tish089nqzz4">
                    <p className="flex items-start gap-3" data-id="element-404" data-mp="2thhj2p2w8mfx">
                      <span className="text-amber-400 flex-shrink-0" data-id="element-405" data-mp="2pj07ntcfwpsp">→</span>
                      <span className="text-stone-100" data-id="element-406" data-mp="339wzvj2gf4z8">
                        Your rate stays the same for as long as you remain a
                        member
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-407" data-mp="3d6i5a5idyi3l">
                      <span className="text-amber-400 flex-shrink-0" data-id="element-408" data-mp="388gndnwp4otp">→</span>
                      <span className="text-stone-100" data-id="element-409" data-mp="38ako5wzat7pr">
                        You get first priority to apply for higher tiers
                        (Precision Cell Ultra)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-410" data-mp="1rtyjsv8uzioi">
                      <span className="text-amber-400 flex-shrink-0" data-id="element-411" data-mp="1rugpeihev993">→</span>
                      <span className="text-stone-100" data-id="element-412" data-mp="1mw6qbwskfoi7">
                        You're part of the inner circle — the people actively
                        applying this intelligence
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}